(function() {
    "use strict"

    const schema = require("./permissions-schema.json")

    const SCOPE_PRECEDENCE = ["sharing", "own_customer", "sub_customer", "other_customer", "dot", "wanco"]

    function PermissionEvaluator(scopesHash, injectedSchema) {
        this.scopes = scopesHash || {}
        this.schema = injectedSchema || schema
        this.cache = {}
    }

    PermissionEvaluator.prototype.can = function(permission, customerId) {
        // Cache key
        const key = customerId != null ? permission + ":" + customerId : permission
        if (this.cache.hasOwnProperty(key)) return this.cache[key]

        const result = this._canUncached(permission, customerId)
        this.cache[key] = result
        return result
    }

    PermissionEvaluator.prototype._canUncached = function(permission, customerId) {
        const permDef = this.schema.p[permission]
        if (!permDef) {
            console.error("Unknown permission: " + permission)
            return false
        }

        // 1. System* short-circuit (matches backend order)
        if (permission.indexOf("System") === 0) {
            return this._checkWancoCapability(permDef)
        }

        // 2. Device gateway check
        if (permission.indexOf("Device") === 0 && permission !== "DeviceRead") {
            if (!this._canUncached("DeviceRead", customerId)) return false
        }

        // 3. Capability check (no customerId) — scan ALL scopes
        if (customerId == null) {
            for (let i = 0; i < SCOPE_PRECEDENCE.length; i++) {
                const scopeType = SCOPE_PRECEDENCE[i]
                const scopeData = this.scopes[scopeType]
                if (!scopeData) continue
                if (permDef.s.indexOf(scopeType) === -1) continue
                if (this._hasBit(scopeData.permissions, permDef)) return true
            }
            return false
        }

        // 4. Resource-scoped check — find matching scope by customerId + scope eligibility
        const matchingScope = this._findMatchingScope(customerId, permDef)
        if (!matchingScope) return false
        return this._hasBit(matchingScope.permissions, permDef)
    }

    PermissionEvaluator.prototype._checkWancoCapability = function(permDef) {
        // System* permissions only valid in wanco scope
        // NOTE: Backend also checks SYSTEM_EVAL_DEPENDENCIES (e.g., SystemCustomerTopDelete
        // requires CustomerRead on target). Frontend omits these intentionally — backend enforces.
        const wancoScope = this.scopes["wanco"]
        if (!wancoScope) return false
        return this._hasBit(wancoScope.permissions, permDef)
    }

    PermissionEvaluator.prototype._findMatchingScope = function(customerId, permDef) {
        // Iterates scope precedence order. For each scope:
        //   1. Skip if permission is not eligible in that scope type (permDef.s check)
        //   2. Check if customerId matches the scope's filter
        // This ensures a permission only matches in scopes where it is valid.
        // Without this check, a permission could false-match in a scope it doesn't belong to.
        if (customerId == null) return null
        customerId = Number(customerId)
        if (isNaN(customerId)) return null

        // 1. own_customer
        const own = this.scopes["own_customer"]
        if (own && permDef.s.indexOf("own_customer") !== -1 && own.filter.customer_id === customerId) {
            return own
        }

        // 2. sub_customer
        const sub = this.scopes["sub_customer"]
        if (sub && permDef.s.indexOf("sub_customer") !== -1 && sub.filter.customer_ids && sub.filter.customer_ids.indexOf(customerId) !== -1) {
            return sub
        }

        // 3. other_customer (exclusion-based — Decision 70)
        const other = this.scopes["other_customer"]
        if (other && permDef.s.indexOf("other_customer") !== -1) {
            const excluded = other.filter.excluded_customer_ids || []
            if (excluded.indexOf(customerId) === -1) {
                return other
            }
        }

        // 4. DOT inference (Decision 2 — process of elimination)
        // If no other scope claimed this customerId, and DOT scope exists,
        // the backend must have authorized this device via DOT access.
        const dot = this.scopes["dot"]
        if (dot && permDef.s.indexOf("dot") !== -1) {
            return dot
        }

        return null
    }

    PermissionEvaluator.prototype._hasBit = function(permissions, permDef) {
        if (!permissions || permDef.b >= permissions.length) return false
        return (permissions[permDef.b] & permDef.m) !== 0
    }

    PermissionEvaluator.prototype.hasScope = function(scopeType) {
        return this.scopes.hasOwnProperty(scopeType)
    }

    PermissionEvaluator.prototype.clearCache = function() {
        this.cache = {}
    }

    window.PermissionEvaluator = PermissionEvaluator
})()
